// The page's native-media switch and the one place that opens the native queue for it (BL-2683, ADR 070 — Ingest
// Protocol § 7.6). Dark by default: `NEXT_PUBLIC_NATIVE_MEDIA_HANDOFF` is set nowhere until the operator has tried
// the hand-off on a phone, and this switch is then deleted in a one-line change.
import {
  NativeCapabilityUnavailableError,
  type NativeDeviceGateway,
  type NativeTransferQueue,
  type OfflineScope,
} from '../../gateways/native-device';

/** @returns whether the page may hand queued photos to the native queue. Off unless the build sets the variable to `true`. */
export function isNativeMediaHandoffEnabled(): boolean {
  return process.env.NEXT_PUBLIC_NATIVE_MEDIA_HANDOFF === 'true';
}

/**
 * @param input.gateway - the device gateway.
 * @param input.scope - the tenant and user.
 * @param input.logError - where a plugin failure is reported.
 * @returns the scope's native queue, or null when the switch is off or the build has no plugin (the foreground then sends every photo).
 * @sideEffect Opens the plugin's queue.
 */
export async function openNativeMediaQueue(input: {
  gateway: NativeDeviceGateway;
  scope: OfflineScope;
  logError?: (message: string, err: Error) => void;
}): Promise<NativeTransferQueue | null> {
  if (!isNativeMediaHandoffEnabled()) return null;
  try {
    return await input.gateway.openNativeTransfer(input.scope);
  } catch (err) {
    if (err instanceof NativeCapabilityUnavailableError) return null;
    input.logError?.('offline sync: the native queue could not be opened', err instanceof Error ? err : new Error(String(err)));
    return null;
  }
}
